"use client";
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import ProductImage from "./ProductImage";
import AddToCart from "./AddToCart";
import AddWishlist from "./AddWishlist";
import BuyNow from "./BuyNow";
import { Check, DatabaseZap, Share2 } from "lucide-react";
import { formatCurrency } from "@/utils/formatCurrency";
import { Badge } from "@/components/ui/badge";
import Link from "next/link";
import StarRating from "@/custom/StarRating";
import { Separator } from "@/components/ui/separator";
import {
  FacebookShareButton,
  FacebookIcon,
  PinterestShareButton,
  PinterestIcon,
  RedditShareButton,
  RedditIcon,
  TelegramShareButton,
  TelegramIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinShareButton,
  LinkedinIcon,
  InstapaperShareButton,
  InstapaperIcon,
  FacebookMessengerShareButton,
  FacebookMessengerIcon,
  EmailShareButton,
  EmailIcon,
} from "next-share";

const ProductInfo = ({ product }) => {
  useEffect(() => {
    if (product) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "view_item",
        ecommerce: {
          currency: "BDT",
          value: product?.discountedPrice,
          items: [
            {
              item_id: product?._id,
              item_name: product?.title,
              item_category: product?.category,
              item_subcategory: product?.subCategory,
              item_type: product?.type || product?.brand,
              item_variant: product?.category || "",
              price: product?.discountedPrice,
              quantity: 1,
            },
          ],
        },
      });
    }
  }, [product]);

  const handleReviewClick = () => {
    const reviewSection = document.getElementById("review-section");
    if (reviewSection) {
      reviewSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const [url, setUrl] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setUrl(window.location.href);
    }
  }, []);

  return (
    <Card className="w-full border-brand-light shadow-brand rounded-2xl overflow-hidden">
      <CardContent className="flex flex-col lg:flex-row p-5 lg:p-10">
        <div className="lg:w-1/2">
          <ProductImage
            product={product}
            className="w-full h-full rounded-xl object-cover object-top transition-transform duration-300 hover:scale-105"
          />
        </div>
        <div className="lg:w-1/2 lg:ml-10 mt-5 lg:mt-0">
          <h1 className="text-3xl font-bold text-brand-navy leading-relaxed">
            {product?.title}
          </h1>
          <p className="text-sm mt-2 font-medium text-muted-foreground">
            {product?.author?.name ? (
              <>
                <span className="text-sm">By: </span>
                <Link
                  href={`/author-books?search=${product?.author?.name}`}
                  className="text-brand-deep hover:text-brand-sky font-medium capitalize transition-colors"
                >
                  {product?.author?.name}
                </Link>
              </>
            ) : (
              <>
                <span className="text-sm">Brand: </span>
                <Link
                  href={`/brand?search=${product?.brand}`}
                  className="text-brand-deep hover:text-brand-sky font-medium capitalize transition-colors"
                >
                  {product?.brand}
                </Link>
              </>
            )}
          </p>
          <p className="text-sm mt-2 text-muted-foreground">
            <span className="text-brand-navy mr-2 font-medium">
              Category:
            </span>
            <span className="text-brand-deep capitalize">
              {product?.category}
            </span>
          </p>
          <div className="text-sm mt-2 flex items-center space-x-4">
            <StarRating rate={product?.rating} size={16} />
            <div className="flex h-5 items-center space-x-2 text-sm text-muted-foreground">
              <div>{Math.max(product?.rating || 0, 0)} Ratings</div>
              <Separator orientation="vertical" className="bg-brand-light" />
              <div
                onClick={handleReviewClick}
                className="hover:text-brand-sky cursor-pointer transition-colors"
              >
                {Math.max(product?.numReviews || 0, 0)} Reviews
              </div>
            </div>
          </div>
          <div className="flex items-center mt-6 mb-4">
            {product?.discount === 0 ? (
              <p className="text-xl font-semibold text-brand-deep">
                {formatCurrency(product?.price)}
              </p>
            ) : (
              <div className="flex items-center gap-4">
                <p className="text-base font-medium text-muted-foreground line-through">
                  {formatCurrency(product?.price)}
                </p>
                <p className="text-xl font-bold text-brand-deep">
                  {formatCurrency(Math.ceil(product?.discountedPrice))}
                </p>
              </div>
            )}
          </div>
          {product?.discount !== 0 && (
            <div className="flex gap-2 mb-4">
              <Badge
                variant="outline"
                className="border-brand-sky text-brand-deep bg-brand-light/40"
              >
                {Math.ceil(product?.discount)}% off
              </Badge>
              <Badge
                variant="outline"
                className="border-green-500 text-green-700 bg-green-50"
              >
                You Save{" "}
                {formatCurrency(
                  Math.ceil(product?.price) -
                    Math.ceil(product?.discountedPrice)
                )}
              </Badge>
            </div>
          )}
          <div className="flex flex-col gap-2 items-start mb-4">
            {product?.countInStock <= 0 ? (
              <>
                <div className="flex items-center gap-2">
                  <DatabaseZap size={16} color="#EA0303" />
                  <span className="font-medium text-destructive">
                    Out of stock
                  </span>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-px">
                <div className="flex items-center gap-2">
                  <div className="bg-green-500 rounded-full p-[2px]">
                    <Check size={16} className="text-white" />
                  </div>
                  <p
                    className={
                      product?.countInStock > 10
                        ? "font-medium text-green-600"
                        : "font-medium text-destructive"
                    }
                  >
                    In Stock (
                    {product?.countInStock > 20 ? "20+" : product?.countInStock}{" "}
                    copies available)
                  </p>
                </div>
                <p className="text-muted-foreground text-xs ml-6">
                  স্টক আউট হওয়ার আগেই অর্ডার করুন
                </p>
              </div>
            )}
            <Badge
              variant="outline"
              className="border-brand-light text-muted-foreground"
            >
              Product Code: {product?.barcode}
            </Badge>
          </div>
          <div
            className={
              product?.countInStock === 0
                ? "hidden"
                : "w-full flex items-center gap-4 mb-4"
            }
          >
            <AddToCart product={product} />
            <BuyNow product={product} />
          </div>
          <div className="flex flex-col gap-2 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-2 items-center text-muted-foreground text-sm">
                <Share2 size={16} />
                Share
              </div>
              <AddWishlist product={product} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <FacebookShareButton
                url={url}
                quote={product?.description}
                hashtag={`#${product?.title}`}
              >
                <FacebookIcon size={32} round />
              </FacebookShareButton>
              <PinterestShareButton url={url} media={product?.title}>
                <PinterestIcon size={32} round />
              </PinterestShareButton>
              <RedditShareButton url={url} title={product?.title}>
                <RedditIcon size={32} round />
              </RedditShareButton>
              <TelegramShareButton url={url} title={product?.title}>
                <TelegramIcon size={32} round />
              </TelegramShareButton>
              <TwitterShareButton url={url} title={product?.title}>
                <TwitterIcon size={32} round />
              </TwitterShareButton>
              <WhatsappShareButton
                url={url}
                title={product?.title}
                separator=":: "
              >
                <WhatsappIcon size={32} round />
              </WhatsappShareButton>
              <LinkedinShareButton url={url}>
                <LinkedinIcon size={32} round />
              </LinkedinShareButton>
              <InstapaperShareButton url={url} title={product?.title}>
                <InstapaperIcon size={32} round />
              </InstapaperShareButton>
              <FacebookMessengerShareButton
                url={url}
                appId={process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID}
              >
                <FacebookMessengerIcon size={32} round />
              </FacebookMessengerShareButton>
              <EmailShareButton
                url={url}
                subject={product?.title}
                body={product?.title}
              >
                <EmailIcon size={32} round />
              </EmailShareButton>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductInfo;